import { API_URL, getFullUrl } from '../utils/config';
import { getToken } from './auth/authService';
import apiClient from '../utils/axiosConfig';

// Configuración para incluir el token en todas las solicitudes
const getAuthHeader = async () => {
  const token = await getToken();
  return token ? { Authorization: `Token ${token}` } : {};
};

// Servicio para gestionar reportes
const reportService = {
  // Obtener todos los reportes de la empresa del usuario
  getEnterpriseReports: async (params = {}) => {
    try {
      const token = await getToken();
      if (!token) {
        console.log('No hay token disponible');
        return [];
      }
      
      const url = `${API_URL}/reports/by-enterprise/`;
      console.log('Haciendo solicitud a:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      });
      
      // Verificar si la respuesta indica error de autenticación
      if (response.status === 401 || response.status === 403) {
        console.log('Error de autenticación detectado en reportService');
        // Disparar evento de token expirado
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
        }
        return [];
      }
      
      if (!response.ok) {
        console.error('Error en respuesta:', response.status, response.statusText);
        return [];
      }
      
      // Obtener el texto de la respuesta
      const responseText = await response.text();
      
      // Convertir el texto a JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing JSON:', responseText);
        return [];
      }
      
      // Verificar que data sea un array
      if (!Array.isArray(data)) {
        console.error('Data is not an array:', data);
        return [];
      }
      
      return data;
    } catch (error) {
      console.error('Error al obtener reportes de la empresa:', error);
      return [];
    }
  },

  // Obtener un reporte por ID
  getReportById: async (id) => {
    try {
      // Hacer la solicitud directamente con fetch para ver la respuesta en bruto
      const token = await getToken();
      const url = `${API_URL}/reports/${id}/`;
      
      console.log('Haciendo solicitud a:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`
        }
      });
      
      // Obtener el texto de la respuesta
      const responseText = await response.text();
      
      // Convertir el texto a JSON
      const data = JSON.parse(responseText);
      
      return data;
    } catch (error) {
      console.error(`Error al obtener reporte ${id}:`, error);
      throw error;
    }
  },
  
  // Obtener reportes por misión
  getReportsByMission: async (missionId) => {
    try {
      const response = await apiClient.get(`/reports/by-mission/${missionId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error al obtener reportes de la misión ${missionId}:`, error);
      throw error;
    }
  },
  
  // Generar tiles bajo demanda
  generateTiles: async (missionId, tifFile = 'NDVI.tif') => {
    try {
      const response = await apiClient.post('/reports/generate-tiles/', {
        mission_id: missionId,
        tif_file: tifFile
      });
      return response.data;
    } catch (error) {
      console.error(`Error al generar tiles para misión ${missionId}:`, error);
      throw error;
    }
  },
  
  // Agregar comentario a la bitácora de hallazgos
  addComment: async (reportId, comment) => {
    try {
      console.log('addComment service called with:', { reportId, comment });
      
      const response = await apiClient.post(`/reports/${reportId}/add-comment/`, {
        comment: comment
      });
      
      console.log('Response:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error al agregar comentario al reporte ${reportId}:`, error);
      console.error('Error details:', error.response?.data);
      throw error;
    }
  },
  
  // Exportar reporte como PDF
  exportReport: async (reportId) => {
    try {
      const token = await getToken();
      const url = `${API_URL}/reports/${reportId}/export/`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al exportar el reporte');
      }
      
      // Obtener el blob del PDF
      const blob = await response.blob();
      
      // Crear URL temporal para descarga
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Crear elemento de descarga temporal
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `reporte_${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL temporal
      window.URL.revokeObjectURL(downloadUrl);
      
      return { success: true };
    } catch (error) {
      console.error(`Error al exportar reporte ${reportId}:`, error);
      throw error;
    }
  }
};

export default reportService;